import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { canManageCompany } from '../types/models';
import { Button } from '../components/ui/Button';
import { RefreshCw } from 'lucide-react';

interface Props {
  children: React.ReactNode;
  /** Require the user to have admin/superadmin in the active company. */
  requireAdmin?: boolean;
}

export function ProtectedRoute({ children, requireAdmin = false }: Props) {
  const {
    user,
    loading,
    isAdmin,
    memberships,
    activeCompanyId,
    activeRole,
    setActiveCompanyId,
    signOutUser,
    refreshMemberships,
    error,
    companyName,
  } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center text-ink2">
        Loading...
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Subcontractors use the Checkover app only, never the website (Bobby, 1 Nov 2026).
  // Someone who is a subcontractor in this company but has another role elsewhere can
  // switch to that company. The server and database rules still decide what any login
  // can read or change; this keeps the website itself to company staff.
  if (activeRole === 'subcontractor') {
    const otherCompanies = memberships.filter((m) => m.role !== 'subcontractor');
    return (
      <div className="flex min-h-screen items-center justify-center bg-hover px-4">
        <div className="w-full max-w-md rounded-lg border border-hair bg-elev p-8 shadow-sm">
          <h1 className="text-xl font-semibold text-ink">Use the Checkover app</h1>
          <p className="mt-3 text-sm text-ink2">
            Subcontractors work in the Checkover app on their phone. The website is for
            the company&rsquo;s own staff.
          </p>
          {otherCompanies.length > 0 && (
            <div className="mt-4 space-y-1.5">
              <p className="text-sm text-ink2">You can open another company you work for:</p>
              {otherCompanies.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setActiveCompanyId(m.companyId)}
                  className="flex w-full items-center justify-between rounded-md border border-hair px-3 py-2 text-sm hover:bg-hover"
                >
                  <span className="font-mono text-xs">{m.companyId}</span>
                  <span className="text-xs capitalize text-ink2">{m.role}</span>
                </button>
              ))}
            </div>
          )}
          <button
            onClick={() => signOutUser()}
            className="mt-6 w-full rounded-md bg-accent px-3 py-2 text-sm font-medium text-white hover:brightness-110"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  if (error || !activeCompanyId || memberships.length === 0 || (requireAdmin && !isAdmin)) {
    const hasAnyMemberships = memberships.length > 0;
    const hasAdminElsewhere = memberships.some((m) => canManageCompany(m.role));

    return (
      <div className="flex min-h-screen items-center justify-center bg-hover px-4">
        <div className="w-full max-w-md rounded-lg border border-hair bg-elev p-8 shadow-sm">
          <h1 className="text-xl font-semibold text-ink">
            {error ? 'Unable to verify access' : 'Access denied'}
          </h1>
          <p className="mt-2 text-sm text-ink2">
            Signed in as{' '}
            <span className="font-medium text-ink">
              {user.email || user.displayName || user.uid}
            </span>
            .
          </p>

          {error && (
            <div className="mt-3 rounded-md tint-danger p-3 text-xs text-st-cancelled">
              <div className="font-medium">Error loading memberships</div>
              <div className="mt-1 break-words">{error}</div>
            </div>
          )}

          {error ? (
            <p className="mt-3 text-sm text-ink2">
              Your sign-in succeeded, but we couldn't verify your company access.
              Please try again.
            </p>
          ) : !hasAnyMemberships ? (
            <p className="mt-3 text-sm text-ink2">
              This account has no active company memberships from{' '}
              <span className="font-mono text-xs">users/{'{uid}'}.companyIds</span>{' '}
              and{' '}
              <span className="font-mono text-xs">
                companies/{'{companyId}'}/members/{'{uid}'}
              </span>
              .
            </p>
          ) : !hasAdminElsewhere ? (
            <>
              <p className="mt-3 text-sm text-ink2">
                You're a member of{' '}
                {memberships.length === 1
                  ? '1 company'
                  : `${memberships.length} companies`}
                , but your role{' '}
                {activeRole ? (
                  <>
                    in{' '}
                    <span className="font-mono text-xs">
                      {activeCompanyId}
                    </span>{' '}
                    is{' '}
                    <span className="font-medium">{activeRole}</span>
                  </>
                ) : (
                  'is limited'
                )}
                . The web console is admin-only - ask a company admin to
                upgrade your role, or sign in with a different account.
              </p>
              <div className="mt-4 rounded-md border border-hair bg-hover p-3 text-xs text-ink2">
                <div className="font-medium text-ink">
                  Your memberships
                </div>
                <ul className="mt-1 space-y-0.5">
                  {memberships.map((m) => (
                    <li key={m.id} className="flex justify-between">
                      <span className="font-mono">{m.companyId}</span>
                      <span className="capitalize text-ink2">
                        {m.role}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          ) : (
            <>
              <p className="mt-3 text-sm text-ink2">
                You do have admin rights in another company. Switch to it
                below.
              </p>
              <div className="mt-4 space-y-1.5">
                {memberships
                  .filter((m) => canManageCompany(m.role))
                  .map((m) => (
                    <button
                      key={m.id}
                      onClick={() => setActiveCompanyId(m.companyId)}
                      className="flex w-full items-center justify-between rounded-md border border-hair px-3 py-2 text-sm hover:bg-hover"
                    >
                      <span className="font-mono text-xs">{m.companyId}</span>
                      <span className="text-xs capitalize text-ink2">
                        {m.role}
                      </span>
                    </button>
                  ))}
              </div>
            </>
          )}

          {!activeCompanyId && memberships.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-sm text-ink2">Choose another company you work for:</p>
              {memberships.map((m) => (
                <Button key={m.companyId} variant="outline" onClick={() => setActiveCompanyId(m.companyId)}>
                  {companyName(m.companyId)}
                </Button>
              ))}
            </div>
          )}

          <div className="mt-6 flex gap-2">
            {error && (
              <Button type="button" variant="outline" icon={RefreshCw} onClick={() => void refreshMemberships()}>
                Try again
              </Button>
            )}
            <button
              onClick={() => signOutUser()}
              className="flex-1 rounded-md bg-accent px-3 py-2 text-sm font-medium text-white hover:brightness-110"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
